import chunk from 'lodash/chunk';
import find from 'lodash/find';
import head from 'lodash/head';
import keyBy from 'lodash/keyBy';
import map from 'lodash/map';
import maxBy from 'lodash/maxBy';
import merge from 'lodash/merge';
import api from '../../api/vtex';

/**
 * It will reduce the product data to a simplified object.
 *
 * @private
 * @param {Object} product The VTEX product data.
 * @returns {Object}
 */
function toSimplifiedProduct(product) {
  const item = head(product.items);
  const seller = find(item.sellers, (sitem) => sitem.default);
  const installments = maxBy(seller.offer.installments, 'total');

  return {
    sku: item.sku,
    ean: item.ean,
    name: product.name,
    brand: product.brand,
    categories: product.categories,
    seller: seller.id,
    quantity: seller.offer.quantity,
    price: {
      current: seller.offer.currentPrice,
      list: seller.offer.listPrice,
    },
    installments: {
      total: installments ? installments.total : 0,
      value: installments ? installments.value : null,
    },
  };
}

export default {
  namespaced: true,
  //
  // State
  state: {
    products: [],
    created: [],
    mounted: [],
  },
  //
  // Getters
  getters: {
    products({ products }) {
      return products;
    },
  },
  //
  // Mutations
  mutations: {
    reset(state) {
      state.created = [];
      state.mounted = [];
    },
    merge(state, products) {
      const merged = merge(keyBy(state.products, 'ean'), keyBy(products, 'ean'));
      const values = Object.values(merged);

      state.products = values;
    },
    addOnCreated(state, ean) {
      state.created = [...state.created, ean];
    },
    addOnMounted(state, ean) {
      state.mounted = [...state.mounted, ean];
    },
  },
  //
  // Actions
  actions: {
    created({ commit }, { ean }) {
      return Promise.resolve(commit('addOnCreated', ean));
    },
    mounted({ state, commit }, { ean, url }) {
      commit('addOnMounted', ean);

      if (state.created.length !== state.mounted.length) {
        return Promise.resolve();
      }

      const maximum = 10;
      const mounted = chunk([...state.mounted], maximum);

      commit('reset');

      return Promise.all(map(mounted, (codes) => api.getProductsByEAN(url, codes)))
        .then((products) => products.flat())
        .then((products) => products.map((product) => toSimplifiedProduct(product)))
        .then((products) => commit('merge', products));
    },
  },
};
